/**
 * Bikefanatics: Ridley / BCF configurator section (sections/bf-configurator.liquid).
 *
 * - Model code comes from data-model-code (product metafield) or, when that
 *   is blank, from the URL: ?model=KANZOFAST7EA and optional ?code=SAVEDCODE.
 * - The 2.4 MB BCF web component is loaded lazily, once per page, either
 *   immediately (data-autoload / hub page) or when the "open" button is pressed.
 * - The component's `add` event ("Add to shoppingcart") fills the hidden
 *   fields of the Shopify contact form and reveals it.
 *
 * language="en" is forced: the component falls back to Dutch for et/fi.
 */
(function () {
  'use strict';

  var SCRIPT_URL = 'https://components.cyclingfactory.be/bcf-customizer/latest/bcf-customizer-incl.min.js';
  var scriptPromise = null;

  function loadScript() {
    if (window.customElements && window.customElements.get('bcf-customizer-incl')) {
      return Promise.resolve();
    }
    if (scriptPromise) return scriptPromise;
    scriptPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = SCRIPT_URL;
      s.async = true;
      s.onload = resolve;
      s.onerror = function () {
        scriptPromise = null;
        reject(new Error('BCF configurator script failed to load'));
      };
      document.head.appendChild(s);
    });
    return scriptPromise;
  }

  function formatPrice(price, locale) {
    if (!price || typeof price.price !== 'number') return '';
    try {
      return new Intl.NumberFormat(locale || 'et-EE', {
        style: 'currency',
        currency: price.currency || 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(price.price);
    } catch (e) {
      return price.price + ' ' + (price.currency || 'EUR');
    }
  }

  function formatDate(value, locale) {
    if (!value) return '';
    var d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(locale || 'et-EE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
    } catch (e) {
      return d.toISOString().slice(0, 10);
    }
  }

  function pickImage(favorite) {
    if (!favorite || !favorite.images) return '';
    var img = favorite.images.primary || favorite.images.print || favorite.images.frame;
    if (!img) return '';
    return img['2x'] || img['1x'] || '';
  }

  function localizedSelections(detail) {
    var out = [];
    var loc = detail.selectionsLocalized;
    if (loc && typeof loc === 'object') {
      Object.keys(loc).forEach(function (key) {
        var item = loc[key];
        if (item && item.value !== undefined) out.push({ name: item.name || key, value: String(item.value) });
      });
    }
    if (!out.length && detail.favorite && detail.favorite.localizedconfiguration) {
      var lc = detail.favorite.localizedconfiguration;
      Object.keys(lc).forEach(function (key) {
        var item = lc[key];
        if (item && item.description && item.description !== 'None') out.push({ name: item.question || key, value: item.description });
      });
    }
    return out;
  }

  function urlParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name) || '';
    } catch (e) {
      return '';
    }
  }

  class BfConfigurator extends HTMLElement {
    connectedCallback() {
      this.widget = this.querySelector('[data-bf-config-widget]');
      this.loading = this.querySelector('[data-bf-config-loading]');
      this.request = this.querySelector('[data-bf-config-request]');
      this.stage = this.querySelector('[data-bf-config-stage]');
      this.openBtn = this.querySelector('[data-bf-config-open]');
      this.locale = this.dataset.locale || 'et';
      this.intlLocale = this.locale === 'fi' ? 'fi-FI' : this.locale === 'en' ? 'en-IE' : 'et-EE';
      this.mounted = false;

      this.modelCode = (this.dataset.modelCode || '').trim();
      this.savedCode = '';
      if (!this.modelCode) {
        this.modelCode = urlParam('model').trim().toUpperCase();
        this.savedCode = urlParam('code').trim();
        if (!this.modelCode) return; // hub page without a chosen model: stay hidden
        this.hidden = false;
        this.setField('model', this.modelCode);
        this.setField('link', window.location.href.split('#')[0]);
        var name = urlParam('name');
        if (name) {
          this.setText('[data-bf-config-model-name]', name);
          this.setField('product', name);
        } else {
          this.setField('product', this.modelCode);
        }
      }

      if (!this.widget) return;

      var self = this;
      if (this.openBtn) {
        this.openBtn.addEventListener('click', function () {
          if (self.stage) self.stage.hidden = false;
          self.openBtn.hidden = true;
          self.mount();
          requestAnimationFrame(function () {
            self.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        });
      }

      var posted = this.request && !this.request.hidden;
      if (this.dataset.autoload === 'true' || posted || !this.openBtn) {
        if (this.stage) this.stage.hidden = false;
        if (this.openBtn) this.openBtn.hidden = true;
        this.mount();
      }

      if (!this.dataset.modelCode && window.location.hash === '#konfiguraator') {
        requestAnimationFrame(function () {
          self.scrollIntoView({ behavior: 'auto', block: 'start' });
        });
      }
    }

    mount() {
      if (this.mounted) return;
      this.mounted = true;
      var self = this;
      loadScript().then(function () {
        var el = document.createElement('bcf-customizer-incl');
        el.setAttribute('model-code', self.modelCode);
        el.setAttribute('language', 'en');
        el.setAttribute('allow-checkout', 'true');
        el.setAttribute('max-delivery-days', self.dataset.maxDeliveryDays || '400');
        el.setAttribute('max-height', self.dataset.maxHeight || '760');
        if (self.savedCode) el.setAttribute('code', self.savedCode);
        el.addEventListener('add', function (ev) { self.onAdd(ev); });
        if (self.loading) self.loading.remove();
        self.widget.appendChild(el);
      }).catch(function () {
        self.mounted = false;
        if (self.loading) {
          self.loading.textContent = self.loading.dataset.error || 'Konfiguraatori laadimine ebaõnnestus. Proovi hiljem uuesti.';
        }
      });
    }

    onAdd(ev) {
      var detail = Array.isArray(ev.detail) ? ev.detail[0] : ev.detail;
      if (!detail) return;

      var favorite = detail.favorite || {};
      var code = favorite.code || '';
      var title = detail.title || '';
      var price = detail.price || favorite.price || null;
      var priceText = formatPrice(price, this.intlLocale);
      var dateText = formatDate(detail.date, this.intlLocale);
      var image = pickImage(favorite);
      var selections = localizedSelections(detail);
      var selectionsText = selections.map(function (s) { return s.name + ': ' + s.value; }).join('\n');
      var selectionCodes = detail.selections ? JSON.stringify(detail.selections) : '';
      var modelCode = (detail.model && detail.model.code) || this.modelCode;
      var version = (detail.model && detail.model.version) || favorite.version || '';

      this.setField('code', code);
      this.setField('title', title + (version ? ' (' + modelCode + ' ' + version + ')' : ''));
      this.setField('price', priceText + (price && price.msrp && price.msrp !== price.price ? ' (MSRP ' + price.msrp + ' ' + price.currency + ')' : ''));
      this.setField('date', dateText);
      this.setField('image', image);
      this.setField('selections', selectionsText);
      this.setField('selection_codes', selectionCodes);

      this.setText('[data-bf-config-title]', title || modelCode);
      this.setText('[data-bf-config-code]', code || '–');
      this.setText('[data-bf-config-price]', priceText || '–');
      var dateRow = this.querySelector('[data-bf-config-date-row]');
      if (dateRow) {
        dateRow.hidden = !dateText;
        this.setText('[data-bf-config-date]', dateText);
      }
      var img = this.querySelector('[data-bf-config-image]');
      if (img) {
        if (image) {
          img.src = image;
          img.alt = title;
          img.hidden = false;
        } else {
          img.hidden = true;
        }
      }
      var list = this.querySelector('[data-bf-config-selections]');
      if (list) {
        list.innerHTML = '';
        selections.forEach(function (s) {
          var li = document.createElement('li');
          var span = document.createElement('span');
          span.textContent = s.name + ': ';
          li.appendChild(span);
          li.appendChild(document.createTextNode(s.value));
          list.appendChild(li);
        });
      }

      if (this.request) {
        this.request.hidden = false;
        var self = this;
        requestAnimationFrame(function () {
          self.request.scrollIntoView({ behavior: 'smooth', block: 'start' });
          var first = self.request.querySelector('input[name="contact[name]"]');
          if (first && !first.value) first.focus({ preventScroll: true });
        });
      }

      this.dispatchEvent(new CustomEvent('bf-configurator:add', { bubbles: true, detail: detail }));
    }

    setField(name, value) {
      var input = this.querySelector('[data-bf-config-field="' + name + '"]');
      if (input) input.value = value || '';
    }

    setText(selector, value) {
      var el = this.querySelector(selector);
      if (el) el.textContent = value || '';
    }
  }

  if (!customElements.get('bf-configurator')) {
    customElements.define('bf-configurator', BfConfigurator);
  }
})();
